import React, { useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Layer,
    PageContent,
    TextArea,
    TextInput,
} from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import { API } from '@enrich/shared-lib';
import { NoteCard } from '../../components/ContactNotesModal/NoteCard';

export const ContactNotes = ({ contactId, notes = [], revalidate }) => {
    const [showModal, setShowModal] = useState();
    const [editingNote, setEditingNote] = useState({});

    const handleAddNote = () => {
        setShowModal(true);
    };

    const handleSaveNote = async (note) => {
        if (editingNote.id) {
            editNote(note);
        } else {
            createNote(note);
        }
    };

    const createNote = async (note) => {
        try {
            await API.post('/api/contact-notes', {
                data: {
                    contact: contactId,
                    message: note.message,
                    subject: note.subject,
                },
            });
            setShowModal(false);
            revalidate();
        } catch (error) {
            console.log(error);
        }
    };

    const editNote = async (note) => {
        try {
            await API.put(`/api/contact-notes/${note.id}`, {
                data: {
                    message: note.message,
                    subject: note.subject,
                },
            });
            setEditingNote();
            setShowModal(false);
            revalidate();
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <PageContent>
            <Box direction="row-responsive" align="center" justify="between">
                <Box>
                    <Heading level="3">Notes</Heading>
                </Box>

                <Box>
                    <Button
                        primary
                        size="large"
                        label="Add Note"
                        onClick={handleAddNote}
                    />
                </Box>
            </Box>

            <Box gap="small" pad="small">
                {showModal && (
                    <NoteModal
                        editingNote={editingNote}
                        saveNote={handleSaveNote}
                        setShowModal={setShowModal}
                    />
                )}
                {[...notes]
                    .sort(
                        (a, b) =>
                            new Date(b.attributes.createdAt) -
                            new Date(a.attributes.createdAt),
                    )
                    .map((note) => (
                        <NoteCard
                            key={note.id}
                            note={note}
                            onEdit={(n) => {
                                setEditingNote(n);
                                setShowModal(true);
                            }}
                        />
                    ))}
            </Box>
        </PageContent>
    );
};

const NoteModal = ({ editingNote, saveNote, setShowModal }) => {
    const modalHeading = editingNote.id ? 'Edit Note' : 'Create Note';
    const [noteValue, setNoteValue] = useState({
        message: '',
        subject: '',
        ...editingNote.attributes,
        id: editingNote.id,
    });

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                pad="medium"
                width="large"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        {modalHeading}
                    </Heading>
                    <Box align="end">
                        <Close
                            onClick={() => {
                                setShowModal(false);
                            }}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>
                    <Form
                        value={noteValue}
                        onChange={(nextValue) => setNoteValue(nextValue)}
                        onReset={() => setNoteValue({})}
                        onSubmit={({ value }) => {
                            saveNote(value);
                        }}>
                        <Box gap="medium">
                            <FormField label="Subject" name="subject">
                                <TextInput name="subject" />
                            </FormField>
                            <FormField label="Message" name="message">
                                <TextArea name="message" />
                            </FormField>
                            <Box align="end">
                                <Button
                                    type="submit"
                                    primary
                                    label="Save"
                                    size="large"
                                />
                            </Box>
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

ContactNotes.propTypes = {
    contactId: PropTypes.string,
    notes: PropTypes.array,
    revalidate: PropTypes.func,
};

NoteModal.propTypes = {
    editingNote: PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.shape({
            createdAt: PropTypes.string,
            message: PropTypes.string,
            subject: PropTypes.string,
        }),
    }),
    saveNote: PropTypes.func,
    setShowModal: PropTypes.func,
};
